import React, { useState } from 'react';
import { useAuth } from '@/components_new/Auth/useAuth';

import './SignUp.css';

interface SignUpProps {
  onSignInClick: () => void;
}

export const SignUp: React.FC<SignUpProps> = ({ onSignInClick }) => {
  const { signUp, isLoading, error } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [schoolName, setSchoolName] = useState('');
  const [localError, setLocalError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLocalError(null);
    setSuccessMessage(null);

    if (password.length < 6) {
      setLocalError('Password must be at least 6 characters');
      return;
    }

    try {
      await signUp({ email, password, name, schoolName });
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Sign up failed';
      // Treat confirmation-required responses as a success info banner, not an error
      if (msg.startsWith('Account created!')) {
        setSuccessMessage(msg);
      } else {
        setLocalError(msg);
      }
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-card">
        <div className="auth-header">
          <h1>📚 Agents of Education</h1>
          <p>Transforming Assessment Into Learning</p>
        </div>

        <form onSubmit={handleSubmit} className="auth-form">
          <h2>Create Account</h2>

          {(localError || error) && !successMessage && (
            <div className="auth-error">
              {localError || error}
            </div>
          )}

          {successMessage && (
            <div className="auth-success" style={{
              background: '#d1fae5',
              border: '1px solid #6ee7b7',
              color: '#065f46',
              borderRadius: '6px',
              padding: '0.75rem 1rem',
              marginBottom: '1rem',
              fontSize: '0.9rem',
            }}>
              ✅ {successMessage}
            </div>
          )}

          <div className="form-group">
            <label htmlFor="name">Full Name</label>
            <input
              id="name"
              type="text"
              placeholder="e.g., John Smith"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              disabled={isLoading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              id="email"
              type="email"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              disabled={isLoading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="schoolName">School (Optional)</label>
            <input
              id="schoolName"
              type="text"
              placeholder="e.g., Lincoln High School"
              value={schoolName}
              onChange={(e) => setSchoolName(e.target.value)}
              disabled={isLoading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">Password</label>
            <input
              id="password"
              type="password"
              placeholder="••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              disabled={isLoading}
              minLength={6}
            />
            <small>At least 6 characters</small>
          </div>

          <button
            type="submit"
            className="auth-button"
            disabled={isLoading}
          >
            {isLoading ? 'Creating Account...' : 'Sign Up'}
          </button>
        </form>

        <div className="auth-footer">
          <p>
            Already have an account?{' '}
            <button
              type="button"
              onClick={onSignInClick}
              className="auth-link"
              disabled={isLoading}
            >
              Sign In
            </button>
          </p>
        </div>
      </div>
    </div>
  );
};

export default SignUp;
